import React, { useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import {
  Globe, ArrowUpRight, Users, FileText, Pencil, Images, X, ChevronLeft, ChevronRight
} from "lucide-react";
import { trackPointer, MediaPlaceholder } from "./UI";
import { getValidUrl, hasCustomImage } from "../utils/Utils";

function ProjectPage({ project, owners, canEdit, onEdit }) {
  const image = hasCustomImage(project.projectImage) ? project.projectImage : null;
  const gallery = (project.gallery || []).filter(hasCustomImage);

  // Índice de la foto abierta a pantalla completa; null = visor cerrado
  const [lightbox, setLightbox] = useState(null);

  const closeLightbox = () => setLightbox(null);
  const moveLightbox = (step) =>
    setLightbox((i) => (i === null ? i : (i + step + gallery.length) % gallery.length));

  // Teclado en el visor: salir con Esc y pasar fotos con las flechas
  useEffect(() => {
    if (lightbox === null) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowRight") moveLightbox(1);
      else if (e.key === "ArrowLeft") moveLightbox(-1);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightbox, gallery.length]);

  return (
    <div className="dyd-page">
      <Container>
        <div className="dyd-project-hero">
          {/* Imagen */}
          <div className="dyd-project-media dyd-rise">
            {image ? (
              <img src={image} alt={project.name} />
            ) : (
              <MediaPlaceholder name={project.name} variant="hero" />
            )}
          </div>

          {/* Datos */}
          <div className="dyd-rise" style={{ "--i": 1 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              {project.category && <span className="dyd-chip">{project.category}</span>}
              {canEdit && (
                <button className="dyd-btn dyd-btn-ghost" onClick={onEdit}>
                  <Pencil size={15} />
                  Editar proyecto
                </button>
              )}
            </div>

            <h1 style={{ margin: "16px 0 20px" }}>{project.name}</h1>

            <div className="dyd-owners">
              <span className="dyd-label"><Users size={12} /> Propietarios</span>
              <div className="dyd-owners-list">
                {owners.length > 0 ? (
                  owners.map((owner) => (
                    <button
                      key={owner._id}
                      className="dyd-owner"
                      onClick={() => (window.location.href = `/profile/${owner._id}`)}
                    >
                      {owner.name} {owner.surname}
                    </button>
                  ))
                ) : (
                  <span className="dyd-hint">Sin usuarios asociados</span>
                )}
              </div>
            </div>

            {project.website && (
              <a
                className="dyd-btn dyd-btn-primary dyd-btn-lg"
                href={getValidUrl(project.website)}
                target="_blank"
                rel="noopener noreferrer"
                style={{ marginTop: 28 }}
              >
                <Globe size={18} />
                Visitar web
                <ArrowUpRight size={16} className="dyd-arrow" />
              </a>
            )}
          </div>
        </div>

        {/* Descripción */}
        <section className="dyd-panel dyd-project-desc dyd-rise" style={{ "--i": 2 }} onMouseMove={trackPointer}>
          <h3 style={{ display: "flex", alignItems: "center", gap: 9, marginTop: 0, marginBottom: 14 }}>
            <FileText size={18} style={{ color: "var(--dyd-blue-light)" }} />
            Descripción
          </h3>
          <p style={{ margin: 0, fontSize: "1rem" }}>
            {project.description || "No hay descripción disponible."}
          </p>
        </section>

        {/* Galería */}
        {gallery.length > 0 && (
          <section className="dyd-panel dyd-project-gallery dyd-rise" style={{ "--i": 3 }}>
            <h3 style={{ display: "flex", alignItems: "center", gap: 9, marginTop: 0, marginBottom: 18 }}>
              <Images size={18} style={{ color: "var(--dyd-blue-light)" }} />
              Galería
              <span className="dyd-chip" style={{ marginLeft: "auto" }}>
                {gallery.length} {gallery.length === 1 ? "foto" : "fotos"}
              </span>
            </h3>

            <div className="dyd-project-gallery-grid">
              {gallery.map((photo, index) => (
                <button
                  key={`${photo}-${index}`}
                  type="button"
                  className="dyd-project-gallery-item"
                  onClick={() => setLightbox(index)}
                  aria-label={`Ampliar imagen ${index + 1} de ${project.name}`}
                >
                  <img src={photo} alt={`${project.name} ${index + 1}`} loading="lazy" />
                </button>
              ))}
            </div>
          </section>
        )}
      </Container>

      {/* Visor a pantalla completa */}
      {lightbox !== null && (
        <div className="dyd-lightbox" onClick={closeLightbox}>
          <button className="dyd-lightbox-close" onClick={closeLightbox} aria-label="Cerrar">
            <X size={20} />
          </button>

          {gallery.length > 1 && (
            <button
              className="dyd-lightbox-nav prev"
              onClick={(e) => { e.stopPropagation(); moveLightbox(-1); }}
              aria-label="Imagen anterior"
            >
              <ChevronLeft size={22} />
            </button>
          )}

          <img
            src={gallery[lightbox]}
            alt={`${project.name} ${lightbox + 1}`}
            onClick={(e) => e.stopPropagation()}
          />

          {gallery.length > 1 && (
            <button
              className="dyd-lightbox-nav next"
              onClick={(e) => { e.stopPropagation(); moveLightbox(1); }}
              aria-label="Imagen siguiente"
            >
              <ChevronRight size={22} />
            </button>
          )}

          <span className="dyd-lightbox-count">{lightbox + 1} / {gallery.length}</span>
        </div>
      )}

      <style>{`
        .dyd-project-hero {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(min(100%, 300px), 1fr));
          gap: 42px;
          align-items: center;
          margin-bottom: 46px;
        }
        .dyd-project-media {
          display: grid;
          place-items: center;
          min-height: 300px;
          max-height: 420px;
          padding: 22px;
          position: relative;
          border-radius: var(--dyd-r-xl);
          overflow: hidden;
          background:
            linear-gradient(135deg, rgba(59,130,246,0.12), rgba(34,211,238,0.07)),
            #0d121b;
          border: 1px solid var(--dyd-border);
          box-shadow: var(--dyd-shadow-md);
        }
        .dyd-project-media img { max-width: 100%; max-height: 380px; object-fit: contain; border-radius: var(--dyd-r-md); }
        /* La portada generada ocupa todo el hueco, sin el margen pensado para el logo */
        .dyd-project-media .dyd-media-ph { position: absolute; inset: 0; }

        .dyd-owners { display: flex; flex-direction: column; gap: 10px; }
        .dyd-owners .dyd-label { display: inline-flex; align-items: center; gap: 6px; }
        .dyd-owners .dyd-label svg { color: var(--dyd-blue-light); }
        .dyd-owners-list { display: flex; flex-wrap: wrap; gap: 8px; }

        .dyd-owner {
          padding: 7px 15px;
          border-radius: 999px;
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--dyd-blue-light);
          background: rgba(59,130,246,0.1);
          border: 1px solid rgba(59,130,246,0.26);
          cursor: pointer;
          transition: transform 0.25s var(--dyd-ease), background 0.25s var(--dyd-ease), color 0.25s var(--dyd-ease);
        }
        .dyd-owner:hover {
          color: #fff;
          background: rgba(59,130,246,0.75);
          transform: translateY(-2px);
        }

        .dyd-project-desc { position: relative; padding: 30px; overflow: hidden; }

        .dyd-project-gallery { padding: 30px; margin-top: 26px; }
        .dyd-project-gallery-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(min(100%, 170px), 1fr));
          gap: 14px;
        }
        .dyd-project-gallery-item {
          padding: 0;
          aspect-ratio: 4 / 3;
          overflow: hidden;
          border-radius: var(--dyd-r-md);
          border: 1px solid var(--dyd-border);
          background: #0d121b;
          cursor: zoom-in;
          transition: transform 0.3s var(--dyd-ease), border-color 0.3s var(--dyd-ease);
        }
        .dyd-project-gallery-item:hover { transform: translateY(-3px); border-color: var(--dyd-border-strong); }
        .dyd-project-gallery-item img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.45s var(--dyd-ease);
        }
        .dyd-project-gallery-item:hover img { transform: scale(1.06); }

        .dyd-lightbox {
          position: fixed;
          inset: 0;
          z-index: 2000;
          display: grid;
          place-items: center;
          padding: 48px 64px;
          background: rgba(4, 6, 10, 0.92);
          backdrop-filter: blur(6px);
          animation: dyd-fade 0.25s var(--dyd-ease);
        }
        .dyd-lightbox img {
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
          border-radius: var(--dyd-r-md);
          box-shadow: 0 30px 80px rgba(0, 0, 0, 0.6);
        }
        .dyd-lightbox-close, .dyd-lightbox-nav {
          position: absolute;
          display: grid;
          place-items: center;
          width: 44px;
          height: 44px;
          border-radius: 50%;
          color: #fff;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.18);
          cursor: pointer;
          transition: background 0.25s var(--dyd-ease), transform 0.25s var(--dyd-ease);
        }
        .dyd-lightbox-close:hover, .dyd-lightbox-nav:hover {
          background: rgba(255, 255, 255, 0.22);
          transform: scale(1.06);
        }
        .dyd-lightbox-close { top: 20px; right: 20px; }
        .dyd-lightbox-nav.prev { left: 14px; }
        .dyd-lightbox-nav.next { right: 14px; }
        .dyd-lightbox-count {
          position: absolute;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--dyd-text-dim);
        }
        @media (max-width: 768px) {
          .dyd-project-hero { gap: 26px; margin-bottom: 32px; }
          .dyd-project-media { min-height: 220px; max-height: 300px; padding: 16px; }
          .dyd-project-media img { max-height: 268px; }
          .dyd-project-desc { padding: 22px 18px; }
          .dyd-project-gallery { padding: 22px 18px; margin-top: 20px; }
          .dyd-project-gallery-grid { gap: 10px; }
        }

        @media (max-width: 640px) {
          .dyd-lightbox { padding: 64px 14px; }
          .dyd-lightbox-nav.prev { left: 8px; }
          .dyd-lightbox-nav.next { right: 8px; }
        }
      `}</style>
    </div>
  );
}

export default ProjectPage;
